import { useEffect, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { Plus, Trash2, Loader2, ArrowLeft, Save, Eye, Code2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const EMPTY_FORM = {
  title: "",
  excerpt: "",
  content: "",
  featured_image: "",
  category: "",
  tags: "",
  author_name: "",
  status: "draft",
  seo_title: "",
  seo_description: "",
};

function PostForm({ post, onSaved, onCancel }) {
  const [form, setForm] = useState(
    post
      ? {
          title: post.title || "",
          excerpt: post.excerpt || "",
          content: post.content || "",
          featured_image: post.featured_image || "",
          category: post.category || "",
          tags: (post.tags || []).join(", "),
          author_name: post.author_name || "",
          status: post.status || "draft",
          seo_title: post.seo_title || "",
          seo_description: post.seo_description || "",
        }
      : EMPTY_FORM
  );
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        excerpt: form.excerpt || null,
        featured_image: form.featured_image || null,
        category: form.category || null,
        author_name: form.author_name || null,
        seo_title: form.seo_title || null,
        seo_description: form.seo_description || null,
      };
      if (post) {
        await api.put(`/admin/blog/posts/${post.id}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/admin/blog/posts", payload);
        toast.success("Post created");
      }
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save post");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card-dark p-6" data-testid="blog-post-form">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold text-white">{post ? "Edit Post" : "New Post"}</h2>
        <button onClick={onCancel} className="text-white/50 hover:text-white text-sm inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Back
        </button>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Title</label>
          <input required value={form.title} onChange={upd("title")} className="input-dark" data-testid="blog-title-input" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Category (optional)</label>
            <input value={form.category} onChange={upd("category")} className="input-dark" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Tags (comma-separated)</label>
            <input value={form.tags} onChange={upd("tags")} placeholder="visa, germany, students" className="input-dark" />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Featured image URL (optional)</label>
          <input value={form.featured_image} onChange={upd("featured_image")} placeholder="https://..." className="input-dark" />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Excerpt (optional)</label>
          <textarea value={form.excerpt} onChange={upd("excerpt")} rows={2} className="input-dark" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs uppercase tracking-widest text-white/55 block">Content (Markdown)</label>
            <button
              type="button"
              onClick={() => setPreview((p) => !p)}
              className="btn-outline-gold text-xs py-1.5 px-3"
              data-testid="blog-preview-toggle"
            >
              {preview ? <Code2 size={12} /> : <Eye size={12} />} {preview ? "Edit" : "Preview"}
            </button>
          </div>
          {preview ? (
            <div className="input-dark min-h-[260px] prose prose-invert prose-sm max-w-none" data-testid="blog-content-preview">
              <ReactMarkdown>{form.content || "*Nothing to preview yet*"}</ReactMarkdown>
            </div>
          ) : (
            <textarea
              required
              value={form.content}
              onChange={upd("content")}
              rows={14}
              placeholder="Write your post in Markdown..."
              className="input-dark font-mono text-sm"
              data-testid="blog-content-input"
            />
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Author (optional)</label>
            <input value={form.author_name} onChange={upd("author_name")} className="input-dark" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Status</label>
            <select value={form.status} onChange={upd("status")} className="input-dark" data-testid="blog-status-select">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>

        <details className="text-sm">
          <summary className="text-xs uppercase tracking-widest text-white/55 cursor-pointer">SEO (optional)</summary>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
            <div>
              <label className="text-xs text-white/50 mb-1.5 block">SEO title</label>
              <input value={form.seo_title} onChange={upd("seo_title")} className="input-dark" />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1.5 block">SEO description</label>
              <input value={form.seo_description} onChange={upd("seo_description")} className="input-dark" />
            </div>
          </div>
        </details>

        <button type="submit" disabled={saving} className="btn-gold text-sm py-2 px-5">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {post ? "Save Changes" : "Create Post"}
        </button>
      </form>
    </div>
  );
}

export default function BlogPanel({ isAdmin }) {
  const [view, setView] = useState("list");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blog/posts");
      setPosts(data.items || []);
    } catch {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  async function deletePost(id) {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/admin/blog/posts/${id}`);
      setPosts((p) => p.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  }

  if (view === "create") {
    return <PostForm onCancel={() => setView("list")} onSaved={() => { setView("list"); loadPosts(); }} />;
  }
  if (view === "edit" && editingPost) {
    return (
      <PostForm
        post={editingPost}
        onCancel={() => setView("list")}
        onSaved={() => { setView("list"); loadPosts(); }}
      />
    );
  }

  return (
    <div className="space-y-3" data-testid="admin-blog-panel">
      <div className="flex justify-end mb-1">
        <button onClick={() => setView("create")} className="btn-gold text-sm py-2 px-4" data-testid="blog-new-button">
          <Plus size={14} /> New Post
        </button>
      </div>
      {loading && <div className="text-center py-12"><Loader2 size={24} className="text-[var(--gold)] animate-spin mx-auto" /></div>}
      {!loading && posts.length === 0 && (
        <div className="card-dark p-12 text-center text-white/55" data-testid="admin-no-blog-posts">
          No posts yet. Create your first one.
        </div>
      )}
      {posts.map((p) => (
        <div key={p.id} className="card-dark p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`blog-row-${p.id}`}>
          <button
            onClick={() => { setEditingPost(p); setView("edit"); }}
            className="text-left flex-1 min-w-0"
          >
            <h3 className="font-semibold text-white truncate">{p.title}</h3>
            <p className="text-xs text-white/50 mt-0.5">{p.category || "Uncategorized"} · {new Date(p.updated_at).toLocaleDateString()}</p>
          </button>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className={`text-xs px-2.5 py-1 rounded-full border ${
                p.status === "published" ? "border-emerald-400/30 text-emerald-400" : "border-white/15 text-white/50"
              }`}
            >
              {p.status === "published" ? "Published" : "Draft"}
            </span>
            {isAdmin && (
              <button
                onClick={() => deletePost(p.id)}
                className="w-8 h-8 grid place-items-center rounded-full text-white/40 hover:text-red-400 hover:bg-red-500/10 transition"
                title="Delete"
                data-testid={`blog-delete-${p.id}`}
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
